import React from 'react';
import { motion } from 'framer-motion';
import { 
  Plus, 
  Search, 
  QrCode, 
  Eye, 
  Edit, 
  Trash2, 
  BarChart, 
  ChevronRight,
  Filter,
  Calendar
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Campaigns = () => {
  const navigate = useNavigate();

  const campaigns = [
    { id: 1, name: 'Diwali Mega Offer', qrCount: 1000, status: 'Active', used: 450, expires: '2026-11-20' },
    { id: 2, name: 'Summer Cashback', qrCount: 500, status: 'Active', used: 120, expires: '2026-06-30' },
    { id: 3, name: 'New Year Special', qrCount: 2000, status: 'Scheduled', used: 0, expires: '2027-01-05' },
    { id: 4, name: 'Weekend Bonanza', qrCount: 300, status: 'Expired', used: 300, expires: '2026-03-15' },
  ];

  return (
    <div className="min-h-screen bg-[#fdfdfd] pt-32 pb-12 px-8 relative">
      <div className="absolute inset-0 luxury-grid-bg pointer-events-none" />
      <div className="max-w-7xl mx-auto relative z-10">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
          <div>
            <h1 className="text-3xl font-serif text-black mb-2">Campaign Management</h1>
            <p className="text-[#666] text-[10px] font-bold uppercase tracking-[0.3em]">Track and optimize your scratch card campaigns</p>
          </div>
          
          <button 
            onClick={() => navigate('/admin/create-campaign')}
            className="luxury-button flex items-center gap-2"
          >
            <Plus size={18} />
            Create New Campaign
          </button>
        </div>

        {/* Filter Bar */}
        <div className="luxury-card p-6 mb-8 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="flex items-center gap-4 w-full md:w-auto">
            <div className="relative flex-1 md:flex-none">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-[#ccc]" size={14} />
              <input 
                type="text" 
                placeholder="Search campaigns..." 
                className="bg-black/[0.02] border border-black/[0.05] rounded-full py-2.5 pl-10 pr-6 text-xs text-black focus:outline-none focus:border-black/10 w-full md:w-64"
              />
            </div>
            <button className="p-2.5 rounded-full bg-black/5 border border-black/[0.05] text-[#999] hover:text-black transition-all">
              <Filter size={18} />
            </button>
          </div>

          <div className="flex gap-4">
            <StatusFilter label="All" count={12} active={true} />
            <StatusFilter label="Active" count={8} active={false} />
            <StatusFilter label="Scheduled" count={2} active={false} />
          </div>
        </div>

        {/* Campaign Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {campaigns.map((camp) => (
            <CampaignCard key={camp.id} camp={camp} />
          ))}
        </div>
      </div>
    </div>
  );
};

const CampaignCard = ({ camp }) => (
  <div className="luxury-card p-8 group hover:border-black/10 transition-all duration-500 relative overflow-hidden">
    {/* Status Badge */}
    <div className={`absolute top-6 right-8 text-[8px] font-black uppercase tracking-widest px-3 py-1 rounded-full ${
      camp.status === 'Active' ? 'bg-emerald-500/10 text-emerald-600' : 
      camp.status === 'Scheduled' ? 'bg-blue-500/10 text-blue-600' : 'bg-black/5 text-[#999]'
    }`}>
      {camp.status}
    </div>

    <div className="mb-8">
      <h3 className="text-xl font-serif text-black mb-2">{camp.name}</h3>
      <div className="flex items-center gap-4 text-[#999] text-[10px] font-bold uppercase tracking-widest">
        <span className="flex items-center gap-1.5"><QrCode size={12} /> {camp.qrCount} Total QRs</span>
        <span className="flex items-center gap-1.5"><Calendar size={12} strokeWidth={3} className="w-3 h-3" /> Expires: {camp.expires}</span>
      </div>
    </div>

    {/* Progress Bar */}
    <div className="mb-10 space-y-3">
      <div className="flex justify-between text-[10px] font-bold uppercase tracking-widest">
        <span className="text-black">Usage Progress</span>
        <span className="text-black">{Math.round((camp.used/camp.qrCount)*100)}% Used</span>
      </div>
      <div className="h-1.5 w-full bg-black/5 rounded-full overflow-hidden">
        <motion.div 
          initial={{ width: 0 }}
          animate={{ width: `${(camp.used/camp.qrCount)*100}%` }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="h-full bg-black"
        />
      </div>
      <div className="flex justify-between text-[8px] font-bold text-[#ccc] uppercase tracking-tighter">
        <span>0 Used</span>
        <span>{camp.qrCount} Total</span>
      </div>
    </div>

    <div className="flex items-center justify-between pt-6 border-t border-black/[0.05]">
      <div className="flex gap-4">
        <button className="text-[#999] hover:text-black transition-colors p-1.5 rounded-lg bg-black/5"><Edit size={16} /></button>
        <button className="text-[#999] hover:text-rose-500 transition-colors p-1.5 rounded-lg bg-black/5"><Trash2 size={16} /></button>
      </div>
      <button className="flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.2em] text-black group-hover:gap-3 transition-all">
        View Analytics <ChevronRight size={14} />
      </button>
    </div>
  </div>
);

const StatusFilter = ({ label, count, active }) => (
  <button className={`px-5 py-2.5 rounded-full text-[10px] font-bold uppercase tracking-widest transition-all ${
    active ? 'bg-black text-[#d4af37] shadow-xl' : 'bg-black/5 text-[#999] hover:text-black'
  }`}>
    {label} <span className={`ml-2 ${active ? 'text-[#d4af37]/50' : 'text-[#ccc]'}`}>{count}</span>
  </button>
);

export default Campaigns;
